import { usePlacementNavigation } from "@design-lab/entities/artwork";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import type { Tab } from "@design-lab/shared/types/tab";
import { useCallback, useEffect, useRef } from "react";

type UseRecaptureOptions = {
  setTab: (tab: Tab) => void;
  isSaveProjectVisible: boolean;
  setIsSaveProjectVisible: (visible: boolean) => void;
};

export function useRecapture({
  setTab,
  isSaveProjectVisible,
  setIsSaveProjectVisible
}: UseRecaptureOptions) {
  const { isFrontArtworkEnhanced } = useValidation();
  const isPendingTabChange = useRef(false);
  const { setActiveSide } = usePlacementNavigation();

  const navigateToTabPostRecaptured = useCallback(() => {
    const targetSide = isFrontArtworkEnhanced ? "front" : "back";
    setTab("design");
    // Why: unconditional single-commit write (MN-9145). The previous code
    // gated the atom write on a render-scoped activeSubTab read and never
    // synced currentPlacement itself, leaving the placement one render behind
    // the recapture navigation.
    setActiveSide(targetSide);
  }, [isFrontArtworkEnhanced, setActiveSide, setTab]);

  const onRecaptured = useCallback(() => {
    if (isSaveProjectVisible) {
      isPendingTabChange.current = true;
      setIsSaveProjectVisible(false);
    } else {
      navigateToTabPostRecaptured();
    }
  }, [isSaveProjectVisible, navigateToTabPostRecaptured, setIsSaveProjectVisible]);

  useEffect(() => {
    if (!isSaveProjectVisible && isPendingTabChange.current) {
      isPendingTabChange.current = false;
      navigateToTabPostRecaptured();
    }
  }, [isSaveProjectVisible, navigateToTabPostRecaptured]);

  return { onRecaptured };
}
